import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Star, Phone, Leaf, Snowflake, CheckCircle, Trees, Hammer, Wind, Sprout, Award, MapPin } from 'lucide-react';

const DoorHangerDoubleSided = () => {
  const [showBack, setShowBack] = useState(false);
  const phoneNumber = "403-919-9057";
  const websiteURL = "https://truenorth-staging.preview.emergentagent.com";
  
  const frontSide = (
    <div className="relative w-[425px] h-[1100px] bg-gray-900 shadow-2xl overflow-hidden">
      
      {/* Door Hanger Hook Cutout */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-16 bg-white z-50">
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-gray-900 rounded-full border-4 border-white"></div>
      </div>

      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
      <div 
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2315803d" fill-opacity="0.2"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }}
      ></div>

      {/* Content */}
      <div className="relative pt-20 px-6 h-full flex flex-col">
        
        {/* Logo */}
        <div className="text-center mb-6 backdrop-blur-xl bg-white/95 rounded-2xl p-4 shadow-2xl border border-white/20">
          <img 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png"
            alt="True North Alberta"
            className="h-20 w-auto mx-auto"
          />
        </div>

        {/* Headline */}
        <div className="text-center mb-6">
          <div className="backdrop-blur-xl bg-gradient-to-br from-emerald-900/90 to-green-800/90 p-6 rounded-2xl shadow-2xl border border-emerald-700/50">
            <h1 className="text-4xl font-bold text-white mb-2">Premium Landscaping</h1>
            <div className="h-1 w-24 bg-gradient-to-r from-emerald-400 to-green-500 mx-auto mb-3 rounded-full"></div>
            <p className="text-emerald-100 text-lg">Transform Your Outdoor Space</p>
            <div className="flex items-center justify-center gap-2 mt-3 text-white/90">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Calgary & Surrounding Areas</span>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="backdrop-blur-xl bg-gradient-to-br from-green-800/80 to-emerald-900/80 p-4 rounded-xl shadow-xl border border-green-700/30">
            <div className="bg-green-600 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
              <Leaf className="w-7 h-7 text-white" />
            </div>
            <p className="text-white font-bold text-sm">Lawn Care &</p>
            <p className="text-white font-bold text-sm">Maintenance</p>
          </div>
          
          <div className="backdrop-blur-xl bg-gradient-to-br from-cyan-900/80 to-blue-900/80 p-4 rounded-xl shadow-xl border border-cyan-700/30">
            <div className="bg-cyan-600 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
              <Snowflake className="w-7 h-7 text-white" />
            </div>
            <p className="text-white font-bold text-sm">Winter Snow</p>
            <p className="text-white font-bold text-sm">Removal</p>
          </div>
          
          <div className="backdrop-blur-xl bg-gradient-to-br from-amber-900/80 to-orange-900/80 p-4 rounded-xl shadow-xl border border-amber-700/30">
            <div className="bg-amber-600 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
              <Trees className="w-7 h-7 text-white" />
            </div>
            <p className="text-white font-bold text-sm">Tree & Shrub</p>
            <p className="text-white font-bold text-sm">Pruning</p>
          </div>
          
          <div className="backdrop-blur-xl bg-gradient-to-br from-stone-800/80 to-gray-900/80 p-4 rounded-xl shadow-xl border border-stone-700/30">
            <div className="bg-stone-600 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
              <Hammer className="w-7 h-7 text-white" />
            </div>
            <p className="text-white font-bold text-sm">Hardscaping &</p>
            <p className="text-white font-bold text-sm">Installation</p>
          </div>
        </div>

        {/* Free Consultation Badge */}
        <div className="backdrop-blur-xl bg-gradient-to-r from-amber-500 to-yellow-500 rounded-2xl p-5 shadow-2xl mb-6 border-2 border-amber-300">
          <div className="text-center">
            <Award className="w-10 h-10 mx-auto mb-2 text-gray-900" />
            <p className="text-2xl font-black text-gray-900 mb-1">FREE CONSULTATION</p>
            <p className="text-sm font-semibold text-gray-800">Professional Assessment</p>
          </div>
        </div>

        {/* Phone CTA */}
        <div className="backdrop-blur-xl bg-white/95 rounded-2xl p-6 shadow-2xl mb-6 border border-white/20">
          <div className="text-center">
            <p className="text-sm font-bold text-green-700 mb-2 uppercase tracking-wider">Call or Text Today</p>
            <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} className="block text-5xl font-black text-green-700 mb-2">
              {phoneNumber}
            </a>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <Phone className="w-4 h-4" />
              <span className="text-sm">Available 7 Days a Week</span>
            </div>
          </div>
        </div>

        {/* 5 Stars */}
        <div className="backdrop-blur-xl bg-white/95 rounded-xl p-4 shadow-xl border border-white/20">
          <div className="flex items-center justify-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-amber-500 text-amber-500" />
            ))}
          </div>
          <p className="text-center text-sm font-bold text-gray-900">Trusted by Calgary Homeowners</p>
          <p className="text-center text-xs text-gray-600 italic mt-1">"Exceptional quality"</p>
        </div>
      </div>
    </div>
  );

  const backSide = (
    <div className="relative w-[425px] h-[1100px] bg-white shadow-2xl overflow-hidden">
      
      {/* Door Hanger Hook */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-16 bg-white z-50">
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-green-700 rounded-full border-4 border-white"></div>
      </div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-br from-green-700 via-emerald-800 to-teal-800"></div>

      {/* Content */}
      <div className="relative pt-20 px-6 h-full flex flex-col">
        
        {/* Title */}
        <div className="text-center mb-6 relative z-10">
          <h2 className="text-3xl font-black text-white mb-2 drop-shadow-lg">COMPLETE SERVICES</h2>
          <div className="h-1 w-32 bg-white mx-auto rounded-full"></div>
        </div>

        {/* Services */}
        <div className="space-y-3 mb-5">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border-l-4 border-green-600 shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-green-600 w-10 h-10 rounded-lg flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Lawn Care & Mowing</p>
                <p className="text-xs text-gray-600">Professional maintenance, edging, trimming</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl border-l-4 border-emerald-600 shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-600 w-10 h-10 rounded-lg flex items-center justify-center">
                <Sprout className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Garden & Bed Maintenance</p>
                <p className="text-xs text-gray-600">Weeding, mulching, seasonal planting</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border-l-4 border-amber-600 shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-amber-600 w-10 h-10 rounded-lg flex items-center justify-center">
                <Trees className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Tree & Shrub Pruning</p>
                <p className="text-xs text-gray-600">Expert trimming and health care</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-xl border-l-4 border-yellow-600 shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-600 w-10 h-10 rounded-lg flex items-center justify-center">
                <Wind className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Spring & Fall Clean-Ups</p>
                <p className="text-xs text-gray-600">Complete seasonal property prep</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-stone-50 to-gray-50 p-4 rounded-xl border-l-4 border-stone-600 shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-stone-600 w-10 h-10 rounded-lg flex items-center justify-center">
                <Hammer className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Hardscaping & Installation</p>
                <p className="text-xs text-gray-600">Patios, walkways, retaining walls</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-xl border-l-4 border-cyan-600 shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-600 w-10 h-10 rounded-lg flex items-center justify-center">
                <Snowflake className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Winter Snow Removal</p>
                <p className="text-xs text-gray-600">24/7 residential & commercial</p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose */}
        <div className="bg-gradient-to-br from-green-700 to-emerald-800 text-white rounded-xl p-4 mb-4 shadow-lg">
          <h3 className="text-center font-bold text-lg mb-3">Why Choose Us?</h3>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Locally Owned</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Fast Response</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Fair Pricing</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Quality Work</span>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-gray-900 text-white rounded-xl p-5 mb-4 shadow-lg">
          <div className="text-center">
            <Phone className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p className="text-xs font-semibold mb-2 text-green-400 uppercase">Call or Text</p>
            <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} className="text-4xl font-black">
              {phoneNumber}
            </a>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-gray-50 rounded-xl p-4 text-center mb-4 border-2 border-green-200">
          <p className="text-xs font-bold text-gray-900 mb-2">VIEW OUR PORTFOLIO</p>
          <div className="flex justify-center mb-2">
            <QRCodeSVG value={websiteURL} size={100} level="H" />
          </div>
          <p className="text-xs text-gray-600">Scan to See Our Work</p>
        </div>

        {/* Logo */}
        <div className="text-center py-3 border-t-2 border-green-200 mt-auto">
          <img 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png"
            alt="True North"
            className="h-12 w-auto mx-auto mb-1"
          />
          <p className="text-xs font-bold text-green-700">TRUE NORTH ALBERTA PROPERTY SERVICES</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
      <div>
        <button 
          onClick={() => setShowBack(!showBack)}
          className="mb-4 bg-green-700 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-green-800 font-semibold mx-auto block"
        >
          {showBack ? 'Show Front Side' : 'Show Back Side'}
        </button>

        {showBack ? backSide : frontSide}

        <button 
          onClick={() => window.print()} 
          className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700 print:hidden font-semibold mx-auto block"
        >
          Print Door Hanger
        </button>
      </div>
    </div>
  );
};

export default DoorHangerDoubleSided;
